/**
 * The ground, drawn (ADR-0022). Display only: it knows nothing about taps or matches.
 *
 * The geometry (where "cover" is, how a line ends, how a wedge is shaped) comes from
 * `@sportsos/engine`'s `wagon.ts`, the same module the server-rendered public page draws from.
 * This file only turns those numbers into React.
 *
 * Flat fills and no animation, because the placement sheet is part of the scoring loop and
 * UI-PRD §1.1 bans gradients, shadows and transitions there. Colours come from theme tokens so
 * the same picture reads in light and dark; a run's value is also carried by line weight and an
 * end marker, since colour alone is not available to everyone.
 */
import {
  drawnAngle,
  GROUND,
  pointAt,
  sectorOfZone,
  sectorPath,
  viewOf,
  ZONES,
} from "@sportsos/engine";
import type { BatterHand, WagonLine } from "@sportsos/engine";

const RUN_COLOUR: Readonly<Record<number, string>> = {
  1: "var(--ds-text-muted)",
  2: "var(--ds-accent)",
  3: "var(--ds-cat-green-mark)",
  4: "var(--ds-cat-teal-mark)",
  6: "var(--ds-cat-violet-mark)",
};

/** A 5 or a 7 is an overthrow to the rope: drawn like the boundary it nearly always is. */
export const runColour = (runs: number): string =>
  RUN_COLOUR[runs] ?? (runs >= 6 ? "var(--ds-cat-violet-mark)" : runs >= 4 ? "var(--ds-cat-teal-mark)" : "var(--ds-accent)");

export const RUN_KEY: readonly { readonly runs: number; readonly label: string }[] = [
  { runs: 1, label: "1" },
  { runs: 2, label: "2" },
  { runs: 3, label: "3" },
  { runs: 4, label: "4" },
  { runs: 6, label: "6" },
];

export interface Cursor {
  readonly angle: number;
  readonly distance: number;
  readonly runs: number;
}

interface Props {
  readonly lines: readonly WagonLine[];
  /** The batter's hand for one batter. `null` for a mixed set, which is drawn normalised. */
  readonly hand: BatterHand | null;
  /** Sector names around the edge and OFF/LEG at the bottom. Off in thumbnails. */
  readonly labels?: boolean;
  /** Shade each sector by the runs drawn through it. */
  readonly shade?: boolean;
  /** The shot being placed, drawn heavier than the rest. */
  readonly cursor?: Cursor | null;
  readonly title: string;
}

export function WagonGround({
  lines,
  hand,
  labels = true,
  shade = false,
  cursor = null,
  title,
}: Props): React.ReactElement {
  const view = viewOf(lines, hand);
  const { centre, radius, inner, size } = GROUND;

  // Shading follows what is drawn, so a mirrored set shades where its lines are.
  const heat = Array.from({ length: 8 }, () => 0);
  for (const line of lines) {
    const sector = Math.floor(drawnAngle(line, view) / 45) % 8;
    heat[sector] = (heat[sector] ?? 0) + line.runs;
  }
  const hottest = Math.max(1, ...heat);

  return (
    <svg
      className="wagon"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={title}
      data-hand={view.hand}
    >
      <circle className="wagon__field" cx={centre} cy={centre} r={radius + 4} />
      <circle className="wagon__inner" cx={centre} cy={centre} r={inner} />
      {shade
        ? heat.map((runs, sector) =>
            runs === 0 ? null : (
              <path
                // Sector index is the identity: there are always exactly eight of them.
                key={sector}
                className="wagon__heat"
                d={sectorPath(sector)}
                style={{ opacity: 0.1 + (0.35 * runs) / hottest }}
              />
            ),
          )
        : null}
      {Array.from({ length: 8 }, (_, sector) => {
        const end = pointAt(sector * 45, 1.03);
        return <line key={sector} className="wagon__spoke" x1={centre} y1={centre} x2={end.x} y2={end.y} />;
      })}
      <rect className="wagon__pitch" x={centre - 5} y={centre - 22} width={10} height={44} rx={1.5} />

      {labels
        ? ZONES.map((zone, index) => {
            const sector = sectorOfZone(index, view.hand);
            const at = pointAt(sector * 45 + 22.5, 0.8);
            return (
              <text key={zone} className="wagon__label" x={at.x} y={at.y + 3} textAnchor="middle">
                {zone}
              </text>
            );
          })
        : null}
      {labels ? (
        <>
          <text className="wagon__end" x={centre} y={13} textAnchor="middle">BOWLER'S END</text>
          <text className="wagon__side" x={view.hand === "left" ? 24 : 276} y={size - 6} textAnchor="middle">OFF</text>
          <text className="wagon__side" x={view.hand === "left" ? 276 : 24} y={size - 6} textAnchor="middle">LEG</text>
        </>
      ) : null}

      {lines.map((line) => {
        const end = pointAt(drawnAngle(line, view), line.distance);
        return (
          <g key={line.eventId}>
            <line
              x1={centre}
              y1={centre}
              x2={end.x}
              y2={end.y}
              stroke={runColour(line.runs)}
              strokeWidth={line.runs >= 4 ? 2.6 : 1.8}
              strokeLinecap="round"
            />
            {line.runs >= 6 ? <circle cx={end.x} cy={end.y} r={3.2} fill={runColour(line.runs)} /> : null}
          </g>
        );
      })}

      {cursor === null ? null : (
        <g>
          <line
            x1={centre}
            y1={centre}
            x2={pointAt(cursor.angle, cursor.distance).x}
            y2={pointAt(cursor.angle, cursor.distance).y}
            stroke={runColour(cursor.runs)}
            strokeWidth={4}
            strokeLinecap="round"
          />
          <circle
            className="wagon__cursor"
            cx={pointAt(cursor.angle, cursor.distance).x}
            cy={pointAt(cursor.angle, cursor.distance).y}
            r={9}
          />
        </g>
      )}
      <circle className="wagon__striker" cx={centre} cy={centre + 16} r={3} />
    </svg>
  );
}

/** 1 · 2 · 3 · 4 · 6 swatches, so a colour is never the only way to read a line. */
export function WagonKey(): React.ReactElement {
  return (
    <div className="wagon-key" aria-label="Line colour by runs">
      {RUN_KEY.map(({ runs, label }) => (
        <span key={runs} className="wagon-key__item">
          <i style={{ background: runColour(runs), height: runs >= 4 ? 4 : 2 }} />
          {label}
        </span>
      ))}
    </div>
  );
}
